










































































































































































































import { InputErrorIcon, OnClickOutside, Scrollbar, TwButton } from '@/app/components';
import { S } from '@/app/utilities';
import { SearchIcon } from '@vue-hero-icons/outline';
import { CheckIcon, PlusCircleIcon, SelectorIcon } from '@vue-hero-icons/solid';
import { computed, defineComponent, nextTick, onUpdated, ref, Ref, watch } from '@vue/composition-api';
import * as R from 'ramda';
import { v4 as uuidv4 } from 'uuid';

export default defineComponent({
    name: 'AdvancedSelect',
    model: {
        prop: 'selected',
        event: 'update-selected',
    },
    props: {
        items: {
            type: Array,
            required: true,
        },
        keyField: {
            type: String,
            default: 'label',
        },
        labelField: {
            type: String,
            default: 'label',
        },
        selected: {
            type: [Array, String, Number],
            default: null,
        },
        rounded: {
            type: String,
            default: 'rounded',
        },
        height: {
            type: String,
            default: 'h-8',
        },
        leadingLabel: {
            type: String,
            default: null,
        },
        leadingLabelWidth: {
            type: String,
            default: 'w-36',
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        errors: {
            type: Array,
            default: null,
        },
        fullError: {
            type: Boolean,
            default: true,
        },
        errorColour: {
            type: String,
            default: 'text-red-700',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        expandInPlace: {
            type: Boolean,
            default: false,
        },
        noResultsMessage: {
            type: String,
        },
        maxSelectedValues: {
            type: Number,
            default: 0, // Zero maxSelectedValues means that if multiple input is selected, no limit to max selected values will be applied
        },
        noOfItems: {
            type: Number,
            required: true,
        },
        allowCustom: {
            type: Boolean,
            default: false,
        },
        searchPlaceholder: {
            type: String,
            default: 'Search items by name...',
        },
        minCharacters: {
            type: Number,
            default: 0,
        },
        itemsAreFiltered: {
            type: Boolean,
            default: false,
        },
        totalItems: {
            type: Number,
            default: 0,
        },
        selectedItems: {
            type: Array,
            default: () => [],
        },
        autoSearch: {
            type: Boolean,
            default: true,
        },
        borderErrorHighlighting: {
            type: Boolean,
            default: false,
        },
        sortAlphabetically: {
            type: Boolean,
            default: true,
        },
        additionalExpandedClasses: { type: String, default: '-mt-8' },
        canUnselect: {
            type: Boolean,
            default: true,
        },
    },
    components: {
        OnClickOutside,
        InputErrorIcon,
        SearchIcon,
        TwButton,
        Scrollbar,
        CheckIcon,
        SelectorIcon,
        PlusCircleIcon,
    },

    setup(props, { emit }) {
        const isOpen = ref<boolean>(false);
        const selectRef = ref<any>(null);
        const itemsRef = ref<any>();
        const search = ref('');
        const paginationStep = ref(1);
        const ready: Ref<boolean> = ref<boolean>(false);
        const searchBoxRef = ref<any>();

        const selectedItemsLabels = computed(() => {
            const selections = [];
            if (!R.isNil(props.selected)) {
                if (props.multiple) {
                    for (let i = 0; i < (props.selected as any[]).length; i++) {
                        let selectedItem: any = allItems.value.find(
                            (item: any) => item[props.keyField] === (props.selected as any[])[i],
                        );
                        if (selectedItem) selections.push(selectedItem[props.labelField]);
                        else {
                            selectedItem = props.selectedItems.find(
                                (item: any) => item[props.keyField] === (props.selected as any[])[i],
                            );
                            if (selectedItem) selections.push(selectedItem[props.labelField]);
                        }
                    }
                } else {
                    let selectedItem: any = allItems.value.find((item: any) => item[props.keyField] === props.selected);
                    if (selectedItem) selections.push(selectedItem[props.labelField]);
                    else {
                        selectedItem = props.selectedItems.find((item: any) => item[props.keyField] === props.selected);
                        if (selectedItem) selections.push(selectedItem[props.labelField]);
                    }
                }
            }
            return selections.length > 0 ? selections.join(', ') : null;
        });

        const close = () => {
            if (!isOpen.value) return;
            search.value = '';
            paginationStep.value = 1;
            emit('search', search.value, paginationStep.value);
            isOpen.value = false;
        };

        const selection = (item: any) => {
            let selectedList;
            if (props.multiple) {
                if (!R.isNil(props.selected) && (props.selected as any[]).includes(item[props.keyField])) {
                    const updatedList = [...(props.selected as any[])];
                    // if user can unselect all value or if they are not able to unselect but has more then 1
                    // values then emit reduced selection list
                    if (updatedList.length > 1 || props.canUnselect) {
                        updatedList.splice((props.selected as any[]).indexOf(item[props.keyField]), 1);
                        emit('update-selected', updatedList);
                        emit('change', updatedList);
                        emit('add', item[props.keyField]);
                    }
                } else if (
                    !props.maxSelectedValues ||
                    (props.maxSelectedValues && (props.selected as any[]).length < props.maxSelectedValues)
                ) {
                    // If there is an input for maxSelectedValues, then limit will be applied
                    const updatedList = [
                        ...(!R.isNil(props.selected as any[]) ? (props.selected as any[]) : []),
                        item[props.keyField],
                    ];
                    emit('update-selected', updatedList);
                    emit('change', updatedList, item[props.keyField]);
                    emit('add', item[props.keyField]);
                } else if (props.maxSelectedValues) {
                    const updatedList = [...(props.selected as any[])];
                    updatedList.splice(0, 1, item[props.keyField]);
                    emit('update-selected', updatedList);
                    emit('change', updatedList);
                    emit('add', item[props.keyField], item);
                }
            } else {
                if (R.isNil(props.selected) || props.selected !== item[props.keyField])
                    selectedList = item[props.keyField];
                // if user can unselect from the dropdown then set value to null. otherwise keep the value
                else selectedList = props.canUnselect ? null : item[props.keyField];
                emit('update-selected', selectedList);
                emit('change', item[props.keyField], item);
            }
            if (!props.multiple || props.maxSelectedValues === 1) {
                close();
            }
        };

        const errorsString = computed(() => {
            const errorStrings = [];
            if (!props.errors || props.errors.length === 0) {
                return null;
            }
            if (props.errors.length === 1) {
                return S.sanitizeHtml(props.errors[0] as string);
            }
            for (let e = 0; e < props.errors.length; e++) {
                const error = props.errors[e];
                errorStrings.push(`<li>${S.sanitizeHtml(error as string)}</li>`);
            }
            return `<ul>${errorStrings.join('')}</ul>`;
        });

        const isSelectable = (item: any) => {
            if (S.has('selectable', item) && item.selectable === false) {
                return false;
            }
            return true;
        };

        const isSelected = (key: string) => {
            if (props.selected && ['String', 'Number'].includes(R.type(props.selected))) {
                return props.selected === key;
            }

            if (props.selected && R.is(Array, props.selected)) {
                return (props.selected as any[]).includes(key);
            }
            return false;
        };

        const disableSelectionAfterMaxLimit = computed(
            () =>
                selectedItemsLabels.value !== null &&
                props.maxSelectedValues > 0 &&
                numberOfItemsSelected.value >= props.maxSelectedValues,
        );

        const numberOfItemsSelected = computed(() => {
            if (!selectedItemsLabels.value) return 0;
            return selectedItemsLabels.value.split(',').length;
        });

        const changeSelection = (item: any) => {
            if (disableSelectionAfterMaxLimit.value && !selectedItemsLabels.value?.includes(item.label)) return;

            if (isSelectable(item) && !props.disabled) {
                selection(item);
            }
        };

        const start = computed<number>(() => (paginationStep.value - 1) * props.noOfItems);
        const end = computed<number>(() => paginationStep.value * props.noOfItems);

        const allItems = computed(() => {
            if (!props.allowCustom) return props.items;
            const customsArray = R.is(Array, props.selected) ? props.selected : props.selected ? [props.selected] : [];
            const customs = customsArray.reduce((acc: any[], s: any) => {
                if (!props.items.some((i: any) => i[props.keyField] === s)) {
                    const obj = {};
                    obj[props.keyField] = s;
                    obj[props.labelField] = s;
                    obj['selectable'] = true;
                    obj['type'] = 'unknown';
                    acc.push(obj);
                }
                return acc;
            }, []);
            return [...props.items, ...customs];
        });

        const filteredItems = computed(() => {
            if (props.itemsAreFiltered) return allItems.value;
            return allItems.value.filter((item: any) =>
                item[props.labelField].toLowerCase().includes(search.value.toLowerCase()),
            );
        });

        const total = computed<number>(() => (props.itemsAreFiltered ? props.totalItems : filteredItems.value.length));

        const sortedItems = computed(() =>
            filteredItems.value.sort((a: any, b: any) => a[props.labelField].localeCompare(b[props.labelField])),
        );

        const visibleItems = computed(() => {
            if (props.itemsAreFiltered) return allItems.value;
            const items = props.sortAlphabetically ? sortedItems.value : filteredItems.value;
            return items.slice(start.value, end.value);
        });

        const maxSelectedItemsReached = (label: string) =>
            disableSelectionAfterMaxLimit.value && !selectedItemsLabels.value?.includes(label);

        const enableNextPagination = computed(() => total.value > props.noOfItems * paginationStep.value);
        const numOfNextItems = computed(() => {
            if (total.value - paginationStep.value * props.noOfItems > props.noOfItems) return props.noOfItems;
            return total.value - paginationStep.value * props.noOfItems;
        });

        const enablePreviousPagination = computed(() => paginationStep.value > 1);

        const loadItems = (next = true) => {
            paginationStep.value = next ? paginationStep.value + 1 : paginationStep.value - 1;
            if (props.itemsAreFiltered) emit('search', search.value, paginationStep.value);
            if (itemsRef.value) itemsRef.value.scrollToTop();
        };

        const searchItems = () => {
            if (search.value.length === 0 || search.value.length >= props.minCharacters) {
                paginationStep.value = 1;
                emit('search', search.value, paginationStep.value);
            }
        };

        const onInput = () => {
            if (props.autoSearch) searchItems();
        };

        const onEnter = () => {
            if (!props.autoSearch) searchItems();
        };

        const itemsInfoText = computed(
            () =>
                `Showing ${start.value + 1} to ${end.value > total.value ? total.value : end.value} of ${
                    total.value
                } items`,
        );
        onUpdated(() => (ready.value = true));

        watch(
            () => [ready.value, isOpen.value],
            ([isReady, isNowOpen]) => {
                if (isReady && isNowOpen) {
                    nextTick(() => {
                        searchBoxRef.value.focus();
                    });
                }
            },
            { immediate: true },
        );

        return {
            selectRef,
            searchBoxRef,
            selectedItemsLabels,
            maxSelectedItemsReached,
            errorsString,
            isOpen,
            close,
            uuidv4,
            selection,
            isSelected,
            isSelectable,
            changeSelection,
            search,
            paginationStep,
            enableNextPagination,
            enablePreviousPagination,
            numOfNextItems,
            loadItems,
            itemsInfoText,
            visibleItems,
            searchItems,
            itemsRef,
            allItems,
            total,
            onEnter,
            onInput,
            disableSelectionAfterMaxLimit,
            numberOfItemsSelected,
        };
    },
});
